/**
 * API Client
 * Handles all API communication with the backend
 */

import { logger } from '@/utils/logger';
import { useCsrfStore } from '@/stores/csrfStore';
const API_URL = import.meta.env.VITE_API_URL || '/api';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get CSRF token from store
    const csrfToken = useCsrfStore.getState().token;

    // Build headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    // Add CSRF token for state-changing operations
    if (options?.method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method)) {
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      // Extract new CSRF token from response headers
      const newCsrfToken = response.headers.get('X-CSRF-Token');
      if (newCsrfToken) {
        useCsrfStore.getState().setToken(newCsrfToken);
        logger.log('[API Client] Updated CSRF token');
      }

      logger.log('[API Client] Response:', {
        status: response.status,
        ok: response.ok,
        data
      });

      if (!response.ok) {
        // Create enhanced error with data for rate limiting
        const error = new Error(data.error || data.message || 'API request failed') as any;
        error.status = response.status;
        error.data = data.data;
        logger.log('[API Client] Throwing error with data:', error.data);
        throw error;
      }

      return data;
    } catch (error) {
      logger.error('API request error:', error);
      throw error;
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * Fetch CSRF token from server
   * Should be called on app initialization
   */
  async fetchCsrfToken(): Promise<string> {
    const url = `${this.baseUrl}/csrf-token`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token');
      }

      const token = await response.text();
      useCsrfStore.getState().setToken(token);
      logger.log('[API Client] CSRF token fetched successfully');

      return token;
    } catch (error) {
      logger.error('[API Client] Failed to fetch CSRF token:', error);
      throw error;
    }
  }
}

export const apiClient = new ApiClient();
